import React, { useEffect, useState, useCallback } from 'react';
import { ArrowRight, Sun, Zap, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';

const Hero = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textVisible, setTextVisible] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Cargar banners desde el API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(`${API_URL}/api/banners/public`);
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) {
            setBanners(data);
          } else {
            setBanners([getDefaultBanner()]);
          }
        }
      } catch (error) {
        console.error('Error cargando banners:', error);
        setBanners([getDefaultBanner()]);
      }
    };
    fetchBanners();
  }, [API_URL]);

  const getDefaultBanner = () => ({
    id: 'default',
    caption: 'Energia Solar en Paraguay',
    title_line1: 'Soberania',
    title_line2: 'Energetica',
    title_line3: 'para tu Hogar',
    subtitle: 'Aprovecha el sol paraguayo para reducir tus costos de electricidad hasta un 85%. Instalaciones premium con tecnologia de vanguardia.',
    background_image_url: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920',
    cta_primary_text: 'Calcular Ahorro',
    cta_primary_link: '/calculadora',
    cta_secondary_text: 'Solicitar Consulta',
    cta_secondary_link: '#contact',
    overlay_opacity: 60,
    accent_color: '#90EE90'
  });

  // Parallax suave con mouse (solo desktop)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (window.innerWidth < 768) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Cambiar banner automaticamente
  const nextBanner = useCallback(() => {
    if (banners.length <= 1) return;
    setTextVisible(false);
    setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
        setIsTransitioning(false);
        setTimeout(() => setTextVisible(true), 100);
      }, 500);
    }, 300);
  }, [banners.length]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(nextBanner, 7000);
    return () => clearInterval(interval);
  }, [banners.length, nextBanner]);

  const goToBanner = (index) => {
    if (index === currentIndex || isTransitioning) return;
    setTextVisible(false);
    setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(index);
        setIsTransitioning(false);
        setTimeout(() => setTextVisible(true), 100);
      }, 500);
    }, 300);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? banners.length - 1 : currentIndex - 1;
    goToBanner(newIndex);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % banners.length;
    goToBanner(newIndex);
  };

  const handleCTAClick = (link) => {
    if (link.startsWith('#')) {
      const element = document.getElementById(link.substring(1));
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = link;
    }
  };

  if (banners.length === 0) {
    return (
      <section className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      </section>
    );
  }

  const currentBanner = banners[currentIndex];
  const accent = currentBanner.accent_color || '#90EE90';

  return (
    <section
      id="home"
      className="relative h-svh min-h-[600px] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Images con efecto parallax */}
      {banners.map((banner, index) => (
        <div
          key={banner.id}
          className={`absolute inset-0 z-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className="absolute inset-[-20px] will-change-transform"
            style={{
              transform: `translate(${mousePos.x * -8}px, ${mousePos.y * -8}px) scale(1.05)`,
              transition: 'transform 0.3s ease-out'
            }}
          >
            <img
              src={banner.background_image_url}
              alt={banner.title_line1}
              className="w-full h-full object-cover"
              style={{ objectPosition: banner.background_position || 'center' }}
            />
          </div>
          {/* Overlay multicapa para profundidad */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
        </div>
      ))}

      {/* Particulas decorativas flotantes */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        <div className="absolute top-[20%] left-[10%] w-1 h-1 bg-white/20 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
        <div className="absolute top-[40%] left-[70%] w-1.5 h-1.5 bg-white/15 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
        <div className="absolute top-[60%] left-[30%] w-1 h-1 bg-white/10 rounded-full animate-float" style={{ animationDelay: '2s', animationDuration: '6s' }}></div>
        <div className="absolute top-[15%] left-[80%] w-0.5 h-0.5 bg-white/20 rounded-full animate-float" style={{ animationDelay: '0.5s', animationDuration: '3.5s' }}></div>
      </div>

      {/* Content principal con efecto 3D */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-20 pb-6 md:pt-24 md:pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Panel de texto con glassmorphism 3D */}
          <div className="lg:col-span-7 xl:col-span-6">
            <div
              className={`relative transition-all duration-700 ease-out ${
                textVisible && !isTransitioning
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{
                transform: textVisible
                  ? `translate(${mousePos.x * 6}px, ${mousePos.y * 4}px)`
                  : 'translateY(32px)',
                transition: 'transform 0.2s ease-out, opacity 0.7s ease-out'
              }}
            >
              {/* Card de cristal con profundidad */}
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden">
                {/* Borde luminoso superior */}
                <div
                  className="absolute top-0 left-0 right-0 h-[2px]"
                  style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
                ></div>

                {/* Fondo glassmorphism */}
                <div className="absolute inset-0 bg-black/30 backdrop-blur-xl"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent"></div>
                {/* Borde sutil */}
                <div className="absolute inset-0 rounded-2xl sm:rounded-3xl border border-white/10"></div>

                {/* Contenido dentro del card */}
                <div className="relative p-5 sm:p-6 md:p-8">
                  {/* Caption */}
                  {currentBanner.caption && (
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Sun style={{ color: accent }} size={18} />
                      <span
                        className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em]"
                        style={{ color: accent }}
                      >
                        {currentBanner.caption}
                      </span>
                    </div>
                  )}

                  {/* Titulo principal con sombra 3D */}
                  <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.1] mb-3 sm:mb-4">
                    <span
                      className="block text-white"
                      style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)' }}
                    >
                      {currentBanner.title_line1}
                    </span>
                    {currentBanner.title_line2 && (
                      <span
                        className="block"
                        style={{
                          color: accent,
                          textShadow: `0 4px 30px ${accent}40, 0 1px 3px rgba(0,0,0,0.3)`,
                        }}
                      >
                        {currentBanner.title_line2}
                      </span>
                    )}
                    {currentBanner.title_line3 && (
                      <span
                        className="block text-white"
                        style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)' }}
                      >
                        {currentBanner.title_line3}
                      </span>
                    )}
                  </h1>

                  {/* Linea decorativa */}
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <div className="h-[2px] w-12 sm:w-16 rounded-full" style={{ backgroundColor: accent }}></div>
                    <div className="h-[2px] w-6 sm:w-8 rounded-full bg-white/30"></div>
                  </div>

                  {/* Subtitulo */}
                  {currentBanner.subtitle && (
                    <p className="text-xs sm:text-sm md:text-base leading-relaxed text-zinc-300 mb-4 sm:mb-6 max-w-lg">
                      {currentBanner.subtitle}
                    </p>
                  )}

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    {currentBanner.cta_primary_text && (
                      <button
                        onClick={() => handleCTAClick(currentBanner.cta_primary_link || '/calculadora')}
                        className="px-5 sm:px-6 py-2.5 sm:py-3 font-bold transition-all duration-300 flex items-center justify-center gap-2 group hover:scale-105 rounded-xl sm:rounded-none text-sm"
                        style={{
                          backgroundColor: accent,
                          color: '#14532D',
                          boxShadow: `0 8px 30px ${accent}40`
                        }}
                        data-testid="hero-calculator-cta"
                      >
                        {currentBanner.cta_primary_text}
                        <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                      </button>
                    )}
                    {currentBanner.cta_secondary_text && (
                      <button
                        onClick={() => handleCTAClick(currentBanner.cta_secondary_link || '#contact')}
                        className="border-2 border-white/40 text-white hover:bg-white hover:text-zinc-900 px-5 sm:px-6 py-2.5 sm:py-3 font-medium transition-all duration-300 backdrop-blur-sm rounded-xl sm:rounded-none text-sm"
                        data-testid="hero-contact-cta"
                      >
                        {currentBanner.cta_secondary_text}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats debajo del card - separados visualmente */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
                {[
                  { icon: Zap, value: '85%', label: 'Ahorro' },
                  { icon: Sun, value: '300+', label: 'Dias de Sol' },
                  { icon: TrendingDown, value: '5-7', label: 'Anos ROI' },
                ].map((stat, idx) => {
                  const StatIcon = stat.icon;
                  return (
                    <div
                      key={idx}
                      className="relative rounded-xl sm:rounded-2xl overflow-hidden group"
                      data-testid={`hero-stat-${idx}`}
                    >
                      <div className="absolute inset-0 bg-black/20 backdrop-blur-md"></div>
                      <div className="absolute inset-0 border border-white/10 rounded-xl sm:rounded-2xl"></div>
                      <div className="relative p-2.5 sm:p-3 text-center">
                        <StatIcon
                          style={{ color: accent }}
                          size={16}
                          className="mx-auto mb-1"
                        />
                        <span className="block text-base sm:text-xl md:text-2xl font-bold text-white">{stat.value}</span>
                        <span className="block text-[9px] sm:text-xs text-zinc-400 mt-0.5">{stat.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Lado derecho - vacio para que la imagen se vea */}
          <div className="hidden lg:block lg:col-span-5 xl:col-span-6"></div>
        </div>
      </div>

      {/* Flechas de navegacion */}
      {banners.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 bg-black/30 hover:bg-black/50 text-white rounded-full transition-all duration-300 backdrop-blur-sm group"
            aria-label="Banner anterior"
            data-testid="hero-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 bg-black/30 hover:bg-black/50 text-white rounded-full transition-all duration-300 backdrop-blur-sm group"
            aria-label="Banner siguiente"
            data-testid="hero-next-btn"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </>
      )}

      {/* Dots */}
      {banners.length > 1 && (
        <div className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToBanner(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex ? 'w-8 h-2' : 'w-2 h-2 hover:bg-white/70'
              }`}
              style={{
                backgroundColor: index === currentIndex ? accent : 'rgba(255,255,255,0.4)'
              }}
              aria-label={`Ir al banner ${index + 1}`}
              data-testid={`hero-dot-${index}`}
            />
          ))}
        </div>
      )}

      {/* Scroll indicator - solo desktop */}
      <div className="hidden sm:block absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center">
            <div className="w-1 h-3 rounded-full mt-2" style={{ backgroundColor: accent }}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
